// Shared Firebase Authentication Configuration
// Link: /src/HelloCashflowDomain/Authentication/services/firebase.ts
// This file provides a centralized Firebase initialization that both apps can use

import { FirebaseApp, initializeApp, getApps, getApp } from "firebase/app";
import { Auth, connectAuthEmulator, getAuth } from "firebase/auth";
import {
    connectFirestoreEmulator,
    Firestore,
    getFirestore,
} from "firebase/firestore";
import {
    connectFunctionsEmulator,
    Functions,
    getFunctions,
} from "firebase/functions";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

const logger = FrontendLoggerFactory.getLogger().withComponent(
    "FirebaseService",
);

// Firebase configuration interface
export interface FirebaseConfig {
    apiKey: string;
    appId: string;
    authDomain: string;
    messagingSenderId: string;
    projectId: string;
    storageBucket: string;
}

// Singleton instances
let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;
let firebaseFirestore: Firestore | null = null;
let firebaseFunctions: Functions | null = null;

// Global initialization promise tracking
let initializationPromise: Promise<void> | null = null;
let resolveInitialization: (() => void) | null = null;

// Create the initialization promise
const createInitializationPromise = () => {
    initializationPromise ??= new Promise<void>(resolve => {
        resolveInitialization = resolve;
    });
    return initializationPromise;
};

/**
 * Initializes Firebase with the provided configuration
 * This function should be called once at the start of your application
 * @param config - Firebase configuration object
 * @param options - Optional configuration for emulators and other settings
 * @returns Firebase services object
 */
export const initializeFirebaseAuth = (
    config?: FirebaseConfig,
    options?: {
        useEmulators?: boolean;
        functionsRegion?: string;
        emulatorPorts?: {
            auth?: number;
            firestore?: number;
            functions?: number;
        };
    },
) => {
    // Create the initialization promise if it doesn't exist
    createInitializationPromise();

    // Use provided config or fall back to environment variables
    const firebaseConfig: FirebaseConfig = config ?? {
        apiKey: import.meta.env.VITE_FIREBASE_APIKEY,
        appId: import.meta.env.VITE_FIREBASE_APPID,
        authDomain: import.meta.env.VITE_FIREBASE_AUTHDOMAIN,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGINGSENDERID,
        projectId: import.meta.env.VITE_FIREBASE_PROJECTID,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGEBUCKET,
    };

    // Validate required configuration
    const requiredFields = ["apiKey", "authDomain", "projectId", "appId"];
    for (const field of requiredFields) {
        if (!firebaseConfig[field as keyof FirebaseConfig]) {
            throw new Error(`Firebase configuration missing: ${field}`);
        }
    }

    // Initialize Firebase app (singleton pattern)
    if (!firebaseApp) {
        const existingApps = getApps();
        const defaultApp = existingApps.find(app => app.name === "[DEFAULT]");
        if (defaultApp) {
            firebaseApp = getApp();
        } else {
            firebaseApp = initializeApp(firebaseConfig);
        }
    }

    // Initialize Auth
    firebaseAuth ??= getAuth(firebaseApp);

    // Initialize Firestore
    firebaseFirestore ??= getFirestore(firebaseApp);

    // Initialize Functions with region
    if (!firebaseFunctions) {
        const region = options?.functionsRegion ?? "australia-southeast1";
        firebaseFunctions = getFunctions(firebaseApp, region);
    }

    // Connect to emulators if specified (for development)
    if (options?.useEmulators && import.meta.env.DEV) {
        const emulatorPorts = options.emulatorPorts ?? {};

        try {
            if (emulatorPorts.auth && firebaseAuth) {
                connectAuthEmulator(
                    firebaseAuth,
                    `http://localhost:${emulatorPorts.auth}`,
                    {
                        disableWarnings: true,
                    },
                );
            }

            if (emulatorPorts.firestore && firebaseFirestore) {
                connectFirestoreEmulator(
                    firebaseFirestore,
                    "localhost",
                    emulatorPorts.firestore,
                );
            }

            if (emulatorPorts.functions && firebaseFunctions) {
                connectFunctionsEmulator(
                    firebaseFunctions,
                    "localhost",
                    emulatorPorts.functions,
                );
            }
        } catch (error) {
            // Emulator connection errors are non-fatal
            logger.warn("Failed to connect to Firebase emulators:", { error });
        }
    }

    // Wait for auth to be ready, then resolve the initialization promise
    if (firebaseAuth && resolveInitialization) {
        firebaseAuth
            .authStateReady()
            .then(() => {
                if (resolveInitialization) {
                    resolveInitialization();
                    resolveInitialization = null; // Prevent multiple resolutions
                }
            })
            .catch(error => {
                logger.error(
                    "Firebase Auth initialization failed:",
                    error as Error,
                );
                if (resolveInitialization) {
                    resolveInitialization(); // Resolve anyway to prevent hanging
                    resolveInitialization = null;
                }
            });
    }

    return {
        app: firebaseApp,
        auth: firebaseAuth,
        firestore: firebaseFirestore,
        functions: firebaseFunctions,
    };
};

/**
 * Returns a promise that resolves when Firebase is fully initialized
 * Components should await this before using any Firebase functionality
 */
export const waitForFirebaseInit = (): Promise<void> => {
    return initializationPromise ?? Promise.resolve();
};

/**
 * Gets the current Firebase Auth instance
 * Throws an error if Firebase hasn't been initialized
 */
export const getFirebaseAuth = (): Auth => {
    if (!firebaseAuth) {
        throw new Error(
            "Firebase Auth not initialized. Call initializeFirebaseAuth() first.",
        );
    }
    return firebaseAuth;
};

/**
 * Gets the current Firebase Firestore instance
 * Throws an error if Firebase hasn't been initialized
 */
export const getFirebaseFirestore = (): Firestore => {
    if (!firebaseFirestore) {
        throw new Error(
            "Firebase Firestore not initialized. Call initializeFirebaseAuth() first.",
        );
    }
    return firebaseFirestore;
};

/**
 * Gets the current Firebase Functions instance
 * Throws an error if Firebase hasn't been initialized
 */
export const getFirebaseFunctions = (): Functions => {
    if (!firebaseFunctions) {
        throw new Error(
            "Firebase Functions not initialized. Call initializeFirebaseAuth() first.",
        );
    }
    return firebaseFunctions;
};

/**
 * Gets the current Firebase App instance
 * Throws an error if Firebase hasn't been initialized
 */
export const getFirebaseApp = (): FirebaseApp => {
    if (!firebaseApp) {
        throw new Error(
            "Firebase not initialized. Call initializeFirebaseAuth() first.",
        );
    }
    return firebaseApp;
};

/**
 * Resets the Firebase instances (useful for testing)
 * WARNING: This should only be used in test environments
 */
export const resetFirebaseInstances = (): void => {
    if (import.meta.env.NODE_ENV !== "test") {
        logger.warn(
            "resetFirebaseInstances should only be used in test environments",
        );
    }

    firebaseApp = null;
    firebaseAuth = null;
    firebaseFirestore = null;
    firebaseFunctions = null;
};

// Default export for convenience
export default {
    getFirebaseApp,
    getFirebaseAuth,
    getFirebaseFirestore,
    getFirebaseFunctions,
    initializeFirebaseAuth,
    resetFirebaseInstances,
};
